import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import { Flame, Activity, Lock, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SignalCard } from "@/components/AISignalsTab";

const HEAT_FLOOR = 40;

/**
 * Merge AI signals + day-trading signals (scalp/day/swing) into one list,
 * keep only score >= HEAT_FLOOR, dedupe by (symbol, signal), sort heat desc.
 */
function mergeHotSignals(aiSignals, dayTrading) {
  const out = [];
  const seen = new Set();
  const push = (s, source) => {
    if (!s || (s.heat_score || 0) < HEAT_FLOOR) return;
    const key = `${s.symbol}|${s.signal}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ ...s, _source: source });
  };
  (aiSignals?.signals || []).forEach((s) => push(s, "AI"));
  (dayTrading?.scalp_signals || []).forEach((s) => push(s, "Scalp"));
  (dayTrading?.day_signals || []).forEach((s) => push(s, "Day"));
  (dayTrading?.swing_signals || []).forEach((s) => push(s, "Swing"));
  out.sort((a, b) => (b.heat_score || 0) - (a.heat_score || 0));
  return out;
}

export default function HotForYouTab({
  isAuthenticated,
  hasSubscription,
  authHeaders,
  onSignIn,
  onSubscribe,
  onTakeTrade,
  onNavigate,
}) {
  const [aiSignals, setAiSignals] = useState(null);
  const [dayTrading, setDayTrading] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    setError(null);
    try {
      const headers = typeof authHeaders === "function" ? authHeaders() : authHeaders;
      const [aiRes, dtRes] = await Promise.all([
        axios.get(`${API}/predictions/signals`, { headers }),
        axios.get(`${API}/predictions/day-trading?style=all`, { headers }),
      ]);
      setAiSignals(aiRes.data);
      setDayTrading(dtRes.data);
    } catch (err) {
      if (err?.response?.status === 402) setError("payment");
      else if (err?.response?.status === 401) setError("signin");
      else setError("Unable to load Hot for you signals");
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, authHeaders]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  if (!isAuthenticated || error === "signin") {
    return (
      <div className="text-center py-12" data-testid="hot-signin-prompt">
        <div className="glass-effect inline-block p-8 rounded-2xl max-w-sm mx-auto">
          <Lock className="w-12 h-12 text-[#FF3B30] mx-auto mb-4" />
          <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: "Unbounded" }}>
            SIGN IN TO SEE HOT FOR YOU
          </h3>
          <p className="text-gray-400 mb-5 text-sm">
            Personalized heat ranks your boosted, watchlisted, and recently-traded symbols across every signal feed.
          </p>
          <Button data-testid="hot-signin-btn" onClick={onSignIn} className="bg-[#FF3B30] text-white hover:bg-[#FF3B30]/90">
            Sign In
          </Button>
        </div>
      </div>
    );
  }

  if (error === "payment") {
    return (
      <div className="text-center py-12" data-testid="hot-paywall-prompt">
        <div className="glass-effect inline-block p-8 rounded-2xl border border-[#F59E0B]/30 max-w-sm mx-auto">
          <Sparkles className="w-12 h-12 text-[#F59E0B] mx-auto mb-4" />
          <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: "Unbounded" }}>
            UNLOCK HOT FOR YOU
          </h3>
          <p className="text-gray-400 mb-5 text-sm">
            Subscribe to surface every personally-relevant signal across the platform in one curated feed.
          </p>
          <Button data-testid="hot-subscribe-btn" onClick={onSubscribe} className="bg-[#F59E0B] text-black hover:bg-[#F59E0B]/90">
            Subscribe
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-center py-12 text-gray-500" data-testid="hot-loading">
        <Activity className="w-8 h-8 mx-auto mb-3 animate-pulse text-[#FF3B30]" />
        Reading the room...
      </div>
    );
  }

  if (error) {
    return <div className="text-center py-12 text-[#FF3B30]" data-testid="hot-error">{error}</div>;
  }

  const hot = mergeHotSignals(aiSignals, dayTrading);
  const boostTtl = dayTrading?.boost_ttl_hours || 168;

  if (hot.length === 0) {
    return (
      <div className="text-center py-16" data-testid="hot-empty">
        <Flame className="w-12 h-12 text-gray-600 mx-auto mb-4" />
        <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: "Unbounded" }}>
          NOTHING HOT FOR YOU YET
        </h3>
        <p className="text-gray-400 text-sm max-w-md mx-auto">
          Add symbols to your watchlist, take a few trades, or unhide a recently-muted asset and they'll start appearing here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="hot-tab-content">
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-effect p-4 rounded-xl border border-[#FF3B30]/30 flex items-center gap-4"
      >
        <Flame className="w-8 h-8 text-[#FF3B30] shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-white" style={{ fontFamily: "Unbounded" }}>
            HOT FOR YOU · {hot.length} signal{hot.length !== 1 ? "s" : ""}
          </div>
          <div className="text-xs text-gray-400">
            Heat ≥ {HEAT_FLOOR}. Boosted, watchlisted, or recently-traded by you.
          </div>
        </div>
      </motion.div>

      <div className="space-y-3">
        {hot.map((sig, index) => (
          <div key={`${sig.symbol}-${sig._source}-${sig.signal}`} className="relative">
            <span
              data-testid={`hot-source-${sig.symbol}`}
              className="absolute top-2 right-2 z-10 px-2 py-0.5 rounded text-[9px] font-bold bg-black/60 text-gray-300 border border-white/10"
            >
              {sig._source}
            </span>
            <SignalCard
              sig={sig}
              index={index}
              isAuthenticated={isAuthenticated}
              onTakeTrade={onTakeTrade}
              onNavigate={onNavigate}
              boostTtlHours={boostTtl}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
